/**
 * Onglet Espace-temps : diagramme de Marey. Distance en x, temps depuis le
 * départ en y. Ce scénario contre le tracé de référence contre la voiture
 * (fourchette heure creuse → heure de pointe). Plus la ligne monte vite, plus
 * c'est lent.
 */
import { useMemo, useState } from "react";
import { communeAt } from "../model/route";
import { useApp } from "../state/store";
import { COULEURS, chemin, echelle, placerVilles, useLargeur } from "./cadre";
import { carLines, fmtMin, interpAt, mareyLine, ticks, timeTicks } from "./series";

const M = { gauche: 46, droite: 14, haut: 36, bas: 24 };
const HAUTEUR = 330;

export function EspaceTemps() {
  const route = useApp((s) => s.route)!;
  const result = useApp((s) => s.result);
  const reference = useApp((s) => s.reference);
  const roads = useApp((s) => s.roads);
  const setHover = useApp((s) => s.setHover);
  const [ref, largeur] = useLargeur<HTMLDivElement>();
  const [survol, setSurvol] = useState<number | null>(null);

  const ligne = useMemo(() => (result ? mareyLine(route, result) : []), [route, result]);
  const ligneRef = useMemo(() => (reference ? mareyLine(route, reference) : []), [route, reference]);
  const voiture = useMemo(() => (roads ? carLines(route, roads) : null), [route, roads]);

  const L = route.length_m / 1000;
  const tMax = Math.max(result ? result.T / 60 : 0, reference ? reference.T / 60 : 0, voiture ? voiture.totalPointe : 0) * 1.06;
  const x0 = M.gauche;
  const x1 = Math.max(x0 + 40, largeur - M.droite);
  const y0 = HAUTEUR - M.bas;
  const y1 = M.haut;
  const sx = echelle(0, L, x0, x1);
  const sy = echelle(0, tMax, y0, y1);

  const pkAt = (clientX: number, rect: DOMRect) => Math.min(L, Math.max(0, ((clientX - rect.left - x0) / (x1 - x0)) * L));
  const onMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const pk = pkAt(e.clientX, e.currentTarget.getBoundingClientRect());
    const idx = Math.min(route.coords.length - 1, Math.round((pk * 1000) / route.step_m));
    setSurvol(idx);
    setHover(idx);
  };
  const onLeave = () => {
    setSurvol(null);
    setHover(null);
  };

  const i = survol;
  const pk = i !== null ? (i * route.step_m) / 1000 : 0;
  const infoX = sx(pk);
  const bande = voiture ? `${chemin(voiture.creuse, sx, sy)}${chemin([...voiture.pointe].reverse(), sx, sy).replace(/^M/, "L")}Z` : "";

  return (
    <section className="graphique" aria-label="Diagramme espace-temps">
      <h2>Espace-temps</h2>
      <ul className="legende" aria-label="Légende">
        <li><span className="cle" style={{ background: COULEURS.scenario }} /> ce scénario{result && <strong> {fmtMin(result.T / 60)}</strong>}</li>
        <li><span className="cle" style={{ background: COULEURS.reference }} /> tracé de référence{reference && <strong> {fmtMin(reference.T / 60)}</strong>}</li>
        {voiture && (
          <li><span className="cle aire" style={{ background: COULEURS.voiture }} /> voiture <strong>{fmtMin(voiture.totalCreuse)} → {fmtMin(voiture.totalPointe)}</strong></li>
        )}
      </ul>
      <div ref={ref} className="cadre">
        {largeur > 0 && (
          <svg width={largeur} height={HAUTEUR} role="img" onPointerMove={onMove} onPointerLeave={onLeave}>
            <title>Temps depuis le départ en fonction de la distance parcourue, train contre voiture</title>
            {timeTicks(tMax).map((t) => (
              <g key={"t" + t}>
                <line x1={x0} x2={x1} y1={sy(t)} y2={sy(t)} stroke={COULEURS.grille} />
                <text x={x0 - 6} y={sy(t) + 4} textAnchor="end" className="axe">{t === 0 ? "0" : fmtMin(t)}</text>
              </g>
            ))}
            {ticks(0, L, 6).map((k) => (
              <text key={"k" + k} x={sx(k)} y={y0 + 16} textAnchor="middle" className="axe">{k} km</text>
            ))}
            {placerVilles(route.landmarks.map((l) => ({ name: l.name, x: sx((l.idx * route.step_m) / 1000) })), x0, x1).map((v) => (
              <g key={v.name}>
                <line x1={v.x} x2={v.x} y1={y1 - 4} y2={y0} stroke={COULEURS.grille} />
                <text x={v.x} y={y1 - 8 - v.dy} textAnchor={v.anchor} className="ville">{v.name}</text>
              </g>
            ))}
            <line x1={x0} x2={x1} y1={y0} y2={y0} stroke={COULEURS.grille} />
            {voiture && (
              <>
                <path d={bande} fill={COULEURS.voiture} fillOpacity={0.14} />
                <path d={chemin(voiture.creuse, sx, sy)} fill="none" stroke={COULEURS.voiture} strokeWidth={1.5} />
                <path d={chemin(voiture.pointe, sx, sy)} fill="none" stroke={COULEURS.voiture} strokeWidth={1.5} />
              </>
            )}
            <path d={chemin(ligneRef, sx, sy)} fill="none" stroke={COULEURS.reference} strokeWidth={2} strokeLinejoin="round" />
            <path d={chemin(ligne, sx, sy)} fill="none" stroke={COULEURS.scenario} strokeWidth={2} strokeLinejoin="round" />
            {result &&
              result.stops.map((s, k) => (
                <circle key={s} cx={sx((s * route.step_m) / 1000)} cy={sy(result.arrivals[k] / 60)} r={3.5} fill={COULEURS.scenario} stroke="#fff" strokeWidth={2} />
              ))}
            {i !== null && (
              <g pointerEvents="none">
                <line x1={infoX} x2={infoX} y1={y1} y2={y0} stroke={COULEURS.encre} />
                {[
                  [ligne, COULEURS.scenario],
                  [ligneRef, COULEURS.reference],
                ].map(([l, c], k) => {
                  const t = interpAt(l as [number, number][], pk);
                  return t === null ? null : <circle key={k} cx={infoX} cy={sy(t)} r={4} fill={c as string} stroke="#fff" strokeWidth={2} />;
                })}
              </g>
            )}
          </svg>
        )}
        {i !== null && (
          <div className="infobulle" style={largeur < 520 ? { left: x0, right: 8 } : { left: infoX + (infoX > largeur * 0.6 ? -12 : 12), transform: infoX > largeur * 0.6 ? "translateX(-100%)" : undefined }}>
            <p><strong>PK {pk.toFixed(1)}</strong> · {communeAt(route, i)}</p>
            {[
              ["ce scénario", interpAt(ligne, pk), COULEURS.scenario],
              ["référence", interpAt(ligneRef, pk), COULEURS.reference],
            ].map(([nom, t, c]) => (
              <p key={nom as string}><span className="cle" style={{ background: c as string }} /> <strong>{t === null ? "—" : fmtMin(t as number)}</strong> {nom as string}</p>
            ))}
            {voiture && (
              <p><span className="cle" style={{ background: COULEURS.voiture }} /> <strong>{fmtMin(interpAt(voiture.creuse, pk) ?? 0)} → {fmtMin(interpAt(voiture.pointe, pk) ?? 0)}</strong> voiture</p>
            )}
          </div>
        )}
      </div>
      <p className="note">
        Plus la ligne monte vite, plus on avance lentement ; chaque marche verticale est un arrêt en gare. Voiture : heure creuse
        d'après OpenStreetMap sans trafic, heure de pointe d'après le dossier du maître d'ouvrage (« plus de 60 minutes » sur les
        trois grands tronçons). Le temps de rabattement jusqu'à la gare n'est pas compté.
      </p>
    </section>
  );
}
